/**
 * Boot system for client initialization
 * 
 * Handles:
 * - Transport registration
 * - State hydration from localStorage
 * - Initial system setup
 * - Default layout initialization for first-time visitors
 * 
 * This is called at application startup to restore persisted state
 * and connect transports for persistence/sync.
 */

import { bus } from './events.js';
import { state, setState, updateState } from './state.js';
import type { PivotState } from './state.js';
import { LocalStorageTransport } from './transports/localStorage.js';
import { log } from '../services/logging/index.js';
import type { GridState } from '../types/square.js';
import type { GridElement } from '../grid-allocation-v2/types.js';

// Flag to track if we're currently restoring views from persistent storage
export let isRestoringPersistedViews = false;
import { uid } from '../utils/helpers.js';
import { updateGutters } from '../utils/viewport.js';
import { AnimationProfileType } from '../types/enums.js';
import { processManager, SINGLETON_PROCESS_IDS } from '../systems/process-manager.js';
import { viewManager } from '../systems/view-manager.js';
// import { IndexedDBTransport } from './transports/indexeddb.js';

/**
 * Create initial grid state
 */
function createInitialGridState(): GridState {
  // Ensure DOM is ready
  if (typeof document === 'undefined') {
    throw new Error('DOM not available - boot must run in browser');
  }

  const gap = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gap')) || 10;
  const gutters = updateGutters();

  return {
    cols: 4,
    rows: 3,
    cellW: 200,
    cellH: 200,
    gap,
    gutterX: gutters.gutterX,
    gutterY: gutters.gutterY,
    width: 0,
    height: 0,
  };
}

// Square factory removed - elements are created by ViewManager

/**
 * Initialize default layout for first-time visitors
 * 
 * Squares are created by Processes via ViewManager.
 */
function initializeDefaultLayout(): void {
  // Squares come from Processes/Views
  // Processes create views, views create squares (if needed)

  // Initialize grid state
  const grid = createInitialGridState();

  // Set default layout
  // Layout is now managed by Processes via Viewport Allocation Manager
  setState('layout', {
    profile: null,
    animationProfile: AnimationProfileType.Smooth,
  });

  setState('grid', grid);
  setState('elements', []); // EMPTY - elements come from Processes/Views only
}

/**
 * Hydrate state from localStorage.
 * Reads persisted state keys and restores them via the EventBus.
 */
function hydrateFromLocalStorage(keys: string[]): void {
  const snapshot: Record<string, unknown> = {};

  for (const k of keys) {
    const raw = localStorage.getItem(`pivot:${k}`);
    if (raw) {
      try {
        snapshot[k] = JSON.parse(raw);
      } catch {
        // Ignore invalid JSON
      }
    }
  }

  if (Object.keys(snapshot).length > 0) {
    // Restore snapshot directly - the restore handler will clear existing state first
    // No need to merge - just pass the snapshot from localStorage
    bus.emit('state.restore', snapshot);
  }
}

/**
 * Boot the client application.
 * 
 * Initializes transports and hydrates state from persistence.
 * If no persisted state exists (first visit), initializes default Eisenhower layout.
 * Call this at application startup.
 */
export function bootClient(): void {

  // 1) Transports
  // Persist any state.* events immediately:
  const ls = new LocalStorageTransport();
  bus.useTransport(ls);

  // Uncomment when IndexedDB support is needed:
  // const idb = new IndexedDBTransport();
  // await idb.connect();
  // bus.useTransport(idb);

  // 2) Hydrate (choose which domains you want to persist between sessions)
  hydrateFromLocalStorage(['user', 'layout', 'grid', 'elements', 'decisions', 'cards', 'ui', 'todo', 'login', 'chat', 'processes', 'viewOrder']);

  // 3) Initialize default layout if first-time visitor
  // Check if layout was restored from localStorage
  if (!state.layout || !state.elements || !state.grid) {
    initializeDefaultLayout();
  } else {
    // Elements are managed by Processes/Views
    // Elements are now managed by Processes/Views only - no normalization needed

    // Ensure elements array exists (empty if no processes/views)
    if (!state.elements) {
      setState('elements', []);
    }
  }

  // 4) Initialize UI state if not present
  if (!state.ui) {
    setState('ui', { selection: null, fps: 0 });
  }

  normalizeSingletonProcesses();
}

/**
 * Restore persisted processes after the UI layer is ready.
 * Call this once the view manager has a layer element (after initApp()).
 */
export function restorePersistedProcesses(): void {
  normalizeSingletonProcesses();

  if (!state.processes || Object.keys(state.processes).length === 0) {
    return;
  }

  // Set flag to indicate we're restoring views from persistent storage
  isRestoringPersistedViews = true;

  setTimeout(() => {
    for (const [processId, processInfo] of Object.entries(state.processes!)) {
      if (processInfo.persist === false) {
        continue;
      }

      try {
        processManager.startProcess(processInfo.kind, processInfo.id);

        if (processInfo.views && processInfo.views.length > 0) {
          setTimeout(() => {
            restoreProcessViewGridInfo(processId, processInfo.views || []);
          }, 200);
        }
      } catch (error) {
        if (state.processes) {
          updateState('processes', (draft) => {
            if (draft) {
              delete draft[processId];
            }
          });
        }
      }
    }

    // Clear restoration flag after a delay to allow all views to mount
    setTimeout(() => {
      isRestoringPersistedViews = false;
    }, 1000);
  }, 0);
}

/**
 * Ensure persisted singleton processes use their canonical IDs.
 * This prevents timestamp-based IDs from respawning duplicate singleton processes on reload.
 */
function normalizeSingletonProcesses(): void {
  if (!state.processes) {
    return;
  }

  const updatedProcesses: Record<string, typeof state.processes[string]> = {};
  let mutated = false;

  for (const [processId, processInfo] of Object.entries(state.processes)) {
    const canonicalId = SINGLETON_PROCESS_IDS[processInfo.kind];
    if (canonicalId) {
      const targetId = canonicalId;
      if (processId !== targetId) {
        mutated = true;
      }

      // Prefer existing canonical entry if it's already present
      if (!updatedProcesses[targetId]) {
        updatedProcesses[targetId] = {
          ...processInfo,
          id: targetId
        };
      }
    } else {
      updatedProcesses[processId] = processInfo;
    }
  }

  if (mutated) {
    setState('processes', updatedProcesses);
  }
}

/**
 * Restore view grid allocation, position, and expanded state for a process
 */
function restoreProcessViewGridInfo(
  processId: string,
  savedViews: Array<{
    id: string;
    gridAllocation?: { cols: number; rows: number };
    gridPosition?: { x: number; y: number };
    expanded?: boolean;
  }>
): void {
  const context = processManager.getProcessContext(processId);
  if (!context) return;
  
  const views = context.getViews?.() || [];
  
  for (const savedView of savedViews) {
    const view = views.find(v => v.id === savedView.id);
    if (!view) continue;
    
    // Restore expanded state
    if (savedView.expanded !== undefined) {
      view.expanded = savedView.expanded;
    }
    
    // Restore grid position and allocation if available
    // This is done by setting preferred position on the element before allocation
    if (savedView.gridPosition && savedView.gridAllocation) {
      const viewElements = viewManager.getViewElements(view.id);
      if (viewElements.length > 0) {
        const element = viewElements[0];
        
        // Set preferred position (used by GridAllocator)
        // Grid allocator will try to place at this position if available
        // Note: This is a simplified restoration - full restoration would need to update acceptableSizes
        // For now, we just trigger reallocation and let the allocator handle it
      }
    }
  }
  
  // Trigger reallocation with preferred positions for all views
  for (const savedView of savedViews) {
    bus.emit('view.needs-allocation', { viewId: savedView.id });
  }

}

